import { Box } from "@mui/material"
import { format, fromUnixTime } from "date-fns"
import { BigNumber } from "ethers"
import { formatUnits } from "ethers/lib/utils"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { AppState } from "src/state"
import { useNavigate } from "react-router-dom"
import Button from "src/components/Button"
import Text from "src/components/Text"
import boostPools from "src/constants/boostPools"
import { calculatePrice } from "src/utils"
import { formatNumber } from "src/utils/parseStringToBigNumber"
import { formatLockPeriod, calculateAccruedFer } from "../../helpers"
import { useVaultsApr } from "../../hooks/useVaultsApr"
import useXFerRate from "../../hooks/useXFerRate"
import { useAction } from "src/hooks/useAction"
import { actions } from "src/state/xFer"
import { useUserFerroBoost } from "src/state/xFer/hooks"
import { FER, XFER } from "src/constants"
import { ChevronRight } from "@mui/icons-material"
import QuestionHelper from "src/components/QuestionHelper"
import { useIsMobile } from "src/hooks/useIsMobile"
import FerroPriceText from "src/components/FerroPriceText"
import useTotalVault from "src/hooks/BoostFarming/useTotalVault"
import {
  useGetBoostedNfts,
  useGetBoostedFarms,
} from "src/hooks/BoostFarming/useFarmBoostContract"
import { useBoolean } from "src/hooks/useBoolean"
import { UnstakeNFTModal, WarnBoostModal } from "./Modal"

interface VaultInnerCardProps {
  poolId: number
  totalDeposited: BigNumber
  totalLocked: BigNumber
  totalUnlocked: BigNumber
  stakeIds: number[]
  withdrawableStakeIds: number[]
  lastUnlockTimestamp: number
  defaultOpened: boolean
}

export default function VaultInnerCard({
  poolId,
  totalDeposited,
  totalLocked,
  totalUnlocked,
  stakeIds,
  withdrawableStakeIds,
  lastUnlockTimestamp,
  defaultOpened,
}: VaultInnerCardProps) {
  const [showMore, setShowMore] = useState(false)
  const { t } = useTranslation()
  const vaultsApr = useVaultsApr()
  const { boostData } = useUserFerroBoost()
  const { pendingFer, stakes } = boostData
  const navigate = useNavigate()
  const toggleContent = useCallback(() => {
    setShowMore((show) => !show)
  }, [])
  const isMobile = useIsMobile()
  const openDepositModal = useAction(actions.setOpenedDepositModal, {
    defaultSelectedPool: poolId,
  })
  const openUpgradeModal = useAction(actions.setOpenedUpgradeModal, {
    amount: totalDeposited.toString(),
    stakeIds,
    poolId,
    lastUnlockTime: lastUnlockTimestamp,
  })
  const openWithdrawModal = useAction(actions.setOpenedWithdrawModal, {
    stakeIds: withdrawableStakeIds,
  })

  const price = useSelector(
    (state: AppState) => state.application.ferro?.price ?? 0,
  )
  const rate = useXFerRate()
  const xFerPrice = price / rate.toUnsafeFloat()

  const totalAccruedFer = calculateAccruedFer(poolId, pendingFer, stakes)

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentPool = boostPools.find((x) => x.pid === poolId)!
  const { lockPeriod, multiplier, imgSrc } = currentPool

  const depositedAmount = formatUnits(totalDeposited, XFER.decimals)
  const lockedAmount = formatUnits(totalLocked, XFER.decimals)
  const unlockedAmount = formatUnits(totalUnlocked, XFER.decimals)
  const lastUnlockDate = format(
    fromUnixTime(lastUnlockTimestamp),
    "MMM dd, yyyy",
  )
  const apr = vaultsApr.find((x) => x.poolId === poolId)?.apr || "0"
  const [showNftModal, openNftModal, closeNftModal] = useBoolean(false)
  const [showBoostModal, openBoostModal, closeBoostModal] = useBoolean(false)

  const totalVault = useTotalVault()
  const { data: stakedNftIds } = useGetBoostedNfts()
  const { data: boostedPoolIds } = useGetBoostedFarms()

  const isUnlockAll = totalUnlocked.eq(totalVault)
  const isNftStaked = stakedNftIds && stakedNftIds?.length > 0
  const isPoolBoosted = boostedPoolIds && boostedPoolIds?.length > 0

  const handleWithdraw = useCallback(() => {
    if (isUnlockAll && isNftStaked) {
      openNftModal()
    } else if (isUnlockAll || isPoolBoosted) {
      openBoostModal()
    } else {
      openWithdrawModal()
    }
  }, [
    isNftStaked,
    isPoolBoosted,
    isUnlockAll,
    openBoostModal,
    openNftModal,
    openWithdrawModal,
  ])

  useEffect(() => {
    if (defaultOpened) {
      setShowMore(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box mb={2} bgcolor="#1A1A1A" borderRadius="10px">
      <Box
        display="flex"
        justifyContent="space-between"
        role="button"
        onClick={toggleContent}
        py="24px"
        px="16px"
        sx={{
          "&:hover": {
            cursor: "pointer",
          },
        }}
      >
        <Box display="flex" alignItems="center">
          <Box mr="8px">
            <img src={imgSrc} />
          </Box>
          <Text bold>{formatLockPeriod(lockPeriod)}</Text>
        </Box>
        <Box display="flex" alignItems="center">
          <img src={XFER.icon} />
          <Text bold mx="8px">
            {formatNumber(depositedAmount, 2, 6)}
          </Text>
          {showMore ? (
            <ChevronRight sx={{ transform: "rotate(-90deg)" }} />
          ) : (
            <ChevronRight sx={{ transform: "rotate(90deg)" }} />
          )}
        </Box>
      </Box>
      {showMore && (
        <Box p={3} borderTop="4px solid #292929">
          <Box display="flex" justifyContent="space-between">
            <Text>APR</Text>
            <Text bold>{formatNumber(apr, 2, 2)}%</Text>
          </Box>
          <Box display="flex" justifyContent="space-between" mt={1}>
            <Text>{t("Multiplier")}</Text>
            <Text>{multiplier / 100}x</Text>
          </Box>
          <Box display="flex" justifyContent="space-between" mt={1}>
            <Text>{t("Total locked xFER")}</Text>
            <Box justifyContent="end">
              <Box display="flex" alignItems="center" justifyContent="flex-end">
                <Text mr={1}>{formatNumber(lockedAmount, 2, 6)}</Text>
                <img src={XFER.icon} />
              </Box>
              <Text size="micro" color="#ADADAD" textAlign="right">
                ~
                {formatNumber(
                  formatUnits(
                    calculatePrice(lockedAmount, xFerPrice, XFER.decimals),
                  ),
                  2,
                  6,
                )}{" "}
                USD
              </Text>
            </Box>
          </Box>
          <Box display="flex" justifyContent="space-between" mt={1}>
            <Text>{t("Total accrued FER")}</Text>
            <Box justifyContent="end">
              <Box display="flex" alignItems="center" justifyContent="flex-end">
                <Text mr={1}>
                  {formatNumber(
                    formatUnits(totalAccruedFer, FER.decimals),
                    2,
                    6,
                  )}
                </Text>
                <img src={FER.icon} />
              </Box>
              <FerroPriceText amount={totalAccruedFer} alignRight />
            </Box>
          </Box>

          <Box display="flex" justifyContent="space-between" mt={1}>
            <Text>{t("Last unlock date")}</Text>
            <Text>{lastUnlockDate}</Text>
          </Box>
          <Box
            justifyContent="space-between"
            alignItems="flex-end"
            my={3}
            display={isMobile ? "block" : "flex"}
            bgcolor="#00000080"
            p={3}
            borderRadius="10px"
          >
            <Box>
              <Box alignItems="center" display="flex">
                <Text color="textSubtle" style={{ whiteSpace: "nowrap" }}>
                  {t("Total xFER unlocked")}
                </Text>
                <QuestionHelper
                  text={t(
                    "You can still keep unlocked xFER in your vault to continue earning rewards.",
                  )}
                  ml="4px"
                />
              </Box>
              <Text bold mt={1}>
                {formatNumber(unlockedAmount, 2, 6)}
              </Text>
            </Box>
            <Button
              disabled={parseFloat(unlockedAmount) === 0}
              onClick={handleWithdraw}
              size="small"
              width={isMobile ? "100%" : "auto"}
              mt={isMobile ? 2 : 0}
            >
              {t("Withdraw")}
            </Button>
          </Box>
          <Box display={isMobile ? "block" : "flex"}>
            {poolId !== 3 && (
              <Button
                onClick={openUpgradeModal}
                fullWidth
                style={{ padding: "12px 20px" }}
              >
                {t("Upgrade Locking Period")}
              </Button>
            )}
            <Button
              onClick={openDepositModal}
              fullWidth
              ml={isMobile ? 0 : 1}
              mt={isMobile ? 1 : 0}
              kind="outline"
            >
              {t("Deposit More")}
            </Button>
          </Box>
        </Box>
      )}
      <WarnBoostModal
        open={showBoostModal}
        onClose={closeBoostModal}
        onConfirm={() => {
          closeBoostModal()
          openWithdrawModal()
        }}
      />
      <UnstakeNFTModal
        open={showNftModal}
        onClose={closeNftModal}
        onConfirm={() => {
          navigate("/pools")
          closeNftModal()
        }}
      />
    </Box>
  )
}
